import { ComponentProps, useMemo } from "react";
import { useTranslation } from "react-i18next";

import LoanApplicationFlowDescription from "@krea/customer-features/shared-components/loan-application-flow-description";
import Preloader from "@krea/customer-features/shared-components/preloader";
import { Text } from "@krea/customer-features/shared-components/text";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import {
  AdviceCategoryEnum,
  LoanApplicationStates,
} from "@krea/customer-features/types/loanApplication";
import { useDebounce } from "@krea/customer-features/utils/useDebounce";
import { AppModal } from "@krea/shared/design-system/modal";

import {
  DigitalAdviceSubmitModalContent,
  ManualAdviceSubmitModalContent,
  RevenueAdviceSubmitModalContent,
} from "../advice/submit-modal-content";
import ProgressBar from "../progress-bar";

interface ISubmitApplicationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  isDone: boolean;
  submitToLenders: () => void;
  goToAdvice: () => void;
}

export const SubmitApplicationModal = ({
  open,
  setOpen,
  isDone,
  goToAdvice,
  submitToLenders,
}: ISubmitApplicationModalProps) => {
  const { t } = useTranslation();
  const { data } = useGetCachedCurrentLoanApplication();

  const { state, adviceCategory } = data ?? {};
  const showNextText = useDebounce(open, 4 * 1000);

  const getPopupTitle = () => {
    if (isDone) {
      return t("iaq.applicationPreview.submit.titleDone");
    } else {
      if (open && showNextText)
        return t("iaq.applicationPreview.submit.title2");
      else return t("iaq.applicationPreview.submit.title1");
    }
  };

  const doneContent = useMemo(() => {
    if (state === LoanApplicationStates.ADVICE) {
      switch (adviceCategory) {
        case AdviceCategoryEnum.DIGITAL_IMPROVEMENTS:
          return (
            <DigitalAdviceSubmitModalContent
              goToAdvice={goToAdvice}
              submitToLenders={submitToLenders}
            />
          );
        case AdviceCategoryEnum.DIGITAL_REVENUE:
          return (
            <RevenueAdviceSubmitModalContent
              goToAdvice={goToAdvice}
              submitToLenders={submitToLenders}
            />
          );
        case AdviceCategoryEnum.COMPLEX:
        case AdviceCategoryEnum.SIMPLE:
          return (
            <ManualAdviceSubmitModalContent
              goToAdvice={goToAdvice}
              submitToLenders={submitToLenders}
            />
          );
        default:
          return (
            <Preloader
              className="tw-mr-2 tw-shrink-0 tw-text-primary"
              size="xl"
            />
          );
      }
    }
  }, [adviceCategory, goToAdvice, state, submitToLenders]);

  const stepsConditions: ComponentProps<
    typeof LoanApplicationFlowDescription
  >["conditions"] = useMemo(
    () => [
      { done: true, active: false },
      { done: open && showNextText, active: open && !showNextText },
      { done: false, active: showNextText },
      { done: false, active: false },
    ],
    [open, showNextText],
  );

  return (
    <AppModal
      open={open}
      setOpen={setOpen}
      title={getPopupTitle()}
      hideCloseButton
    >
      {!isDone ? (
        <>
          <div className="tw-rounded tw-bg-gray tw-p-4">
            <div className="tw-mb-2 tw-flex tw-items-center">
              <Preloader
                className="tw-mr-2 tw-shrink-0 tw-text-primary"
                size="md"
              />

              <Text size="sm" className="tw-font-bold tw-text-gray-600">
                {t("iaq.applicationPreview.popup.preloader")}
              </Text>
            </div>

            <ProgressBar isCompleted={isDone} />
          </div>

          <div className="tw-mt-4 tw-rounded tw-border tw-px-6 tw-py-4">
            <LoanApplicationFlowDescription
              useConditions
              conditions={stepsConditions}
            />
          </div>
        </>
      ) : (
        doneContent
      )}
    </AppModal>
  );
};
